import React, { useState, useEffect } from "react"
import { Box, Button, Select, MenuItem } from "@material-ui/core"
import { useSnackbar } from "notistack"
import { WarningPanel } from "../common/WarningPanel"
import { useEsriModules } from "../common/useEsriModules"
import { getFeatureLayerRestUrl } from "../featuer-layer-settings/esriDefinitionHelpers"

interface MissingDisplayFieldAlertProps {
  layer: __esri.FeatureLayer | undefined
  onSuccess?: () => void
}

export const MissingDisplayFieldAlert: React.FC<MissingDisplayFieldAlertProps> = ({
  layer,
  onSuccess,
}) => {
  /** Track whether to show the fields */
  const [possibleFields, setPossibleFields] = useState<
    undefined | __esri.Field[]
  >()
  useEffect(() => {
    layer?.when().then((loadedLayer: __esri.FeatureLayer) => {
      if (!loadedLayer.displayField) {
        setPossibleFields(loadedLayer.fields)
      }
    })
  }, [layer])

  /** Set the display field */
  const { enqueueSnackbar } = useSnackbar()
  const [displayField, setDisplayField] = useState<string | unknown>("")
  const [request] = useEsriModules<[typeof __esri.request]>(["esri/request"])
  const updateLayerWithDisplayField = () => {
    // generate form body for request
    const body = new FormData()
    body.append("f", "json")
    body.append("updateDefinition", JSON.stringify({ displayField }))

    // use esriRequest to perform a rest request
    request &&
      layer &&
      request(getFeatureLayerRestUrl(layer) + "/updateDefinition", {
        responseType: "json",
        authMode: "immediate",
        method: "post",
        body,
      })
        .then(() => {
          enqueueSnackbar("Display field set", { variant: "success" })
          setPossibleFields(undefined)
          onSuccess && onSuccess()
        })
        .catch(error => {
          enqueueSnackbar("Error setting the display field", {
            variant: "error",
          })
          console.log(error)
        })
  }

  return possibleFields ? (
    <WarningPanel
      warning="This feature layer is missing a display field. This tells the app which field to use as the title of the
    feature. Set it below."
    >
      <Box mb={2}>
        <Select
          variant="outlined"
          color="secondary"
          fullWidth
          value={displayField}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
            setDisplayField(e.target.value)
          }
        >
          <MenuItem disabled>Select display field</MenuItem>
          {possibleFields
            .filter(field => field.editable)
            .map(field => (
              <MenuItem key={field.name} value={field.name}>
                {field.alias}
              </MenuItem>
            ))}
        </Select>
      </Box>
      <Button
        color="secondary"
        variant="contained"
        onClick={updateLayerWithDisplayField}
      >
        Set display field
      </Button>
    </WarningPanel>
  ) : null
}
